/**
 * Reconcile DISPLAYED scheduling keys with the PERSISTED intake record.
 *
 * Form initialization derives values for display that may not exist on the
 * intake (see useFormInitialization):
 *   - visit type falls back to 'soc' when legacy `kinnser_create_episode` is
 *     true (e.g. set by the "New Patient" confirmation) but
 *     `kinnser_visit_type` was never persisted, and
 *   - creation type falls back to 'current_episode' when the visit type is ROC.
 *
 * The Push to EMR gate (pushMode.ts) and the backend scheduling endpoints read
 * the persisted intake.fields, not the form — so a derived-but-never-persisted
 * visit type strands the intake: the Scheduling tab looks complete (SOC shown
 * selected, so the user never fires the visit-type onChange, the only other
 * writer) while Push to EMR reports "Complete these steps: Scheduling" and the
 * backend would reject the push with 400.
 *
 * This builds the patch that closes that gap: persist a displayed value only
 * where the intake has none. It never overwrites a persisted value (mid-edit
 * changes are owned by the selects' onChange saves) and only applies to
 * existing patients — scheduling keys are existing-patient data, and the
 * "New Patient" confirmation intentionally nulls them.
 *
 * Returns null when nothing needs persisting.
 */
export function buildDerivedSchedulingKeysPatch({
  isExistingPatient,
  intakeVisitType,
  intakeCreationType,
  formVisitType,
  formCreationType,
}: {
  isExistingPatient: boolean;
  intakeVisitType: string | null | undefined;
  intakeCreationType: string | null | undefined;
  formVisitType: string | null | undefined;
  formCreationType: string | null | undefined;
}): {
  kinnser_visit_type?: 'soc' | 'roc';
  kinnser_creation_type?: 'new_admission' | 'new_episode' | 'current_episode';
} | null {
  if (!isExistingPatient) return null;

  const patch: {
    kinnser_visit_type?: 'soc' | 'roc';
    kinnser_creation_type?: 'new_admission' | 'new_episode' | 'current_episode';
  } = {};

  if (
    !intakeVisitType &&
    (formVisitType === 'soc' || formVisitType === 'roc')
  ) {
    patch.kinnser_visit_type = formVisitType;
  }

  const effectiveVisitType = intakeVisitType || formVisitType;
  if (
    !intakeCreationType &&
    (effectiveVisitType === 'soc' || effectiveVisitType === 'roc') &&
    (formCreationType === 'new_admission' ||
      formCreationType === 'new_episode' ||
      formCreationType === 'current_episode')
  ) {
    patch.kinnser_creation_type = formCreationType;
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
